import React, { useEffect, useMemo, useState } from "react";
import { Tree } from "antd";
import type { TreeProps, DataNode } from "antd/es/tree";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { formatJapaneseDateValue } from "@lhd/hooks";
dayjs.extend(customParseFormat);

/* ---------- 1. Build treeData (Year → Month → Day → Hour) ---------- */
const FMT_SEC = "YYYY年MM月DD日 HH:mm:ss";
const FMT_MIN = "YYYY年MM月DD日 HH:mm";
const FMT_HOUR = "YYYY年MM月DD日 HH";
const FMT_MIN_NOSPACE = "YYYY年MM月DD日HH:mm";
const FMT_HOUR_NOSPACE = "YYYY年MM月DD日HH";
const FMT_DAY = "YYYY年MM月DD日";
const FMT_MONTH = "YYYY年MM月";
const FMT_YEAR = "YYYY年";

const parse = (s: string) => {
  let d = dayjs(
    s,
    [
      FMT_SEC,
      FMT_MIN,
      FMT_HOUR,
      FMT_MIN_NOSPACE,
      FMT_HOUR_NOSPACE,
      FMT_DAY,
      "YYYY年M月D日",
      "YYYY年MM月D日",
      "YYYY年M月DD日",
      FMT_MONTH,
      "YYYY年M月",
      FMT_YEAR,
    ],
    true,
  );
  if (!d.isValid()) d = dayjs(s);
  return d;
};

// Detect level of timestamp
type DateLevel = "year" | "month" | "day" | "hour" | "minute" | "second";

function detectLevel(item: string | number | Date): DateLevel {
  if (typeof item !== "string") return "second";

  const s = item.trim();

  if (/^\d{4}年$/.test(s)) return "year";
  if (/^\d{4}年\d{1,2}月$/.test(s)) return "month";
  if (/^\d{4}年\d{1,2}月\d{1,2}日$/.test(s)) return "day";
  if (/^\d{4}年\d{1,2}月\d{1,2}日\s?\d{1,2}時?$/.test(s)) return "hour";
  if (/^\d{4}年\d{1,2}月\d{1,2}日\s?\d{1,2}:\d{1,2}分?$/.test(s))
    return "minute";
  if (/^\d{4}年\d{1,2}月\d{1,2}日\d{1,2}:\d{1,2}$/.test(s)) return "minute";

  return "second";
}

function getMaxLevel(levels: DateLevel[]): DateLevel {
  const order: DateLevel[] = [
    "year",
    "month",
    "day",
    "hour",
    "minute",
    "second",
  ];
  let maxIdx = 0;
  levels.forEach((level) => {
    const idx = order.indexOf(level);
    if (idx > maxIdx) maxIdx = idx;
  });
  return order[maxIdx];
}

export function buildTimeTreeData(
  src: (string | number | Date)[],
  parseClockTime = false,
): {
  tree: DataNode[];
  parent: Map<string, string | null>;
  children: Map<string, string[]>;
  leafKeys: string[];
  leafValueMap: Map<string, Set<string>>;
} {
  const bucket: Record<number, Record<number, Set<number>>> = {};
  const leafValueMap = new Map<string, Set<string>>();

  const pad = (n: number) => (n < 10 ? "0" + n : "" + n);

  for (const item of src) {
    let h: number;
    let mi: number;
    let s: number;
    let str: string;

    if (parseClockTime && typeof item === "string") {
      const match = /^(\d+)(?::([0-5]\d))?$/.exec(item.trim());
      if (!match) continue;
      h = Number(match[1]);
      if (!Number.isSafeInteger(h)) continue;
      mi = Number(match[2] ?? 0);
      s = 0;
      str = item;
    } else {
      const d = dayjs(item);
      if (!d.isValid()) continue;
      h = d.hour();
      mi = d.minute();
      s = d.second();
      str = typeof item === "string" ? item : d.toISOString();
    }

    const miKey = `${pad(h)}-${pad(mi)}`;
    const set = leafValueMap.get(miKey) || new Set();
    set.add(str);
    leafValueMap.set(miKey, set);

    bucket[h] = bucket[h] || {};
    bucket[h][mi] = bucket[h][mi] || new Set();
    bucket[h][mi].add(s);
  }

  const parent = new Map<string, string | null>();
  const children = new Map<string, string[]>();
  const leafKeys: string[] = [];
  const hours: DataNode[] = [];
  const addChild = (p: string, c: string | string[]) =>
    children.set(p, (children.get(p) || []).concat(c));

  Object.keys(bucket)
    .map(Number)
    .sort((a, b) => b - a)
    .forEach((h) => {
      const hKey = `${pad(h)}`;
      parent.set(hKey, null);

      const minNodes: DataNode[] = [];
      Object.keys(bucket[h])
        .map(Number)
        .sort((a, b) => a - b)
        .forEach((mi) => {
          const miKey = `${hKey}-${pad(mi)}`;
          parent.set(miKey, hKey);
          addChild(hKey, miKey);

          minNodes.push({ key: miKey, title: `${pad(mi)}分`, isLeaf: true });
          leafKeys.push(miKey);
        });

      hours.push({ key: hKey, title: `${pad(h)}時`, children: minNodes });
      addChild(
        hKey,
        minNodes.map((n) => String(n.key)),
      );
    });

  return { tree: hours, parent, children, leafKeys, leafValueMap };
}

export function buildTreeData(
  src: (string | number | Date)[],
  mode?: string,
): {
  tree: DataNode[];
  parent: Map<string, string | null>;
  children: Map<string, string[]>;
  leafKeys: string[];
} {
  // Detect max level from input data
  const levels = src.map((item) => detectLevel(item));
  const maxLevel =
    mode === "date"
      ? "day"
      : mode === "date_and_time" ||
        mode === "time" ||
        mode === "created_datetime" ||
        mode === "updated_datetime"
      ? "minute"
      : getMaxLevel(levels);

  /* bucket[Y][M][D][H][MIN] = Set<SEC> */
  const bucket: Record<
    number,
    Record<number, Record<number, Record<number, Record<number, Set<number>>>>>
  > = {};

  /* ─── Collect ─────────────────────────────────────────────────── */
  for (const item of src) {
    const d = typeof item === "string" ? parse(item) : dayjs(item);

    if (!d.isValid()) continue;

    const level = detectLevel(item);

    const y = d.year(),
      m = d.month() + 1,
      dd = d.date(),
      h = d.hour(),
      mi = d.minute(),
      s = d.second();

    bucket[y] = bucket[y] || {};

    if (level === "year") {
      bucket[y][1] = bucket[y][1] || {};
      bucket[y][1][1] = bucket[y][1][1] || {};
      bucket[y][1][1][0] = bucket[y][1][1][0] || {};
      bucket[y][1][1][0][0] = bucket[y][1][1][0][0] || new Set();
      bucket[y][1][1][0][0].add(0);
      continue;
    }

    bucket[y][m] = bucket[y][m] || {};

    if (level === "month") {
      bucket[y][m][1] = bucket[y][m][1] || {};
      bucket[y][m][1][0] = bucket[y][m][1][0] || {};
      bucket[y][m][1][0][0] = bucket[y][m][1][0][0] || new Set();
      bucket[y][m][1][0][0].add(0);
      continue;
    }

    bucket[y][m][dd] = bucket[y][m][dd] || {};

    if (level === "day") {
      bucket[y][m][dd][0] = bucket[y][m][dd][0] || {};
      bucket[y][m][dd][0][0] = bucket[y][m][dd][0][0] || new Set();
      bucket[y][m][dd][0][0].add(0);
      continue;
    }

    bucket[y][m][dd][h] = bucket[y][m][dd][h] || {};

    if (level === "hour") {
      bucket[y][m][dd][h][0] = bucket[y][m][dd][h][0] || new Set();
      bucket[y][m][dd][h][0].add(0);
      continue;
    }

    bucket[y][m][dd][h][mi] = bucket[y][m][dd][h][mi] || new Set();

    if (level === "minute") {
      bucket[y][m][dd][h][mi].add(0);
      continue;
    }

    bucket[y][m][dd][h][mi].add(s);
  }

  const pad = (n: number) => (n < 10 ? "0" + n : "" + n);

  const parent = new Map<string, string | null>();
  const children = new Map<string, string[]>();
  const leafKeys: string[] = [];
  const years: DataNode[] = [];
  const addChild = (p: string, c: string | string[]) =>
    children.set(p, (children.get(p) || []).concat(c));

  /* ─── Build tree ──────────────────────────────────────────────── */
  Object.keys(bucket)
    .sort((a, b) => +b - +a)
    .forEach((yStr) => {
      const yKey = yStr;
      parent.set(yKey, null);

      // If max level is year, make year a leaf
      if (maxLevel === "year") {
        years.push({
          key: yKey,
          title: `${yStr}年`,
          isLeaf: true,
        });
        leafKeys.push(yKey);
        return;
      }

      const monthNodes: DataNode[] = [];

      Object.keys(bucket[+yStr])
        .sort((a, b) => +a - +b)
        .forEach((mStr) => {
          const mKey = `${yKey}-${pad(+mStr)}`;
          parent.set(mKey, yKey);
          addChild(yKey, mKey);

          // If max level is month, make month a leaf
          if (maxLevel === "month") {
            monthNodes.push({
              key: mKey,
              title: `${mStr}月`,
              isLeaf: true,
            });
            leafKeys.push(mKey);
            return;
          }

          const dayNodes: DataNode[] = [];

          Object.keys(bucket[+yStr][+mStr])
            .sort((a, b) => +a - +b)
            .forEach((dStr) => {
              const dKey = `${mKey}-${pad(+dStr)}`;
              parent.set(dKey, mKey);
              addChild(mKey, dKey);

              // If max level is day, make day a leaf
              if (maxLevel === "day") {
                dayNodes.push({
                  key: dKey,
                  title: `${dStr}日`,
                  isLeaf: true,
                });
                leafKeys.push(dKey);
                return;
              }

              const hourNodes: DataNode[] = [];

              Object.keys(bucket[+yStr][+mStr][+dStr])
                .sort((a, b) => +a - +b)
                .forEach((hStr) => {
                  const hKey = `${dKey}-${pad(+hStr)}`;
                  parent.set(hKey, dKey);
                  addChild(dKey, hKey);

                  // If max level is hour, make hour a leaf
                  if (maxLevel === "hour") {
                    hourNodes.push({
                      key: hKey,
                      title: `${hStr}時`,
                      isLeaf: true,
                    });
                    leafKeys.push(hKey);
                    return;
                  }

                  const minuteMap = bucket[+yStr][+mStr][+dStr][+hStr];
                  const minuteKeys = Object.keys(minuteMap).map(Number);

                  const needMinute =
                    minuteKeys.some((mi) => mi !== 0) ||
                    maxLevel === "minute" ||
                    maxLevel === "second";
                  const isOnlyHourZero = +hStr === 0 && !needMinute;

                  if (needMinute) {
                    /* -------- Minute level -------- */
                    const minNodes: DataNode[] = [];

                    minuteKeys
                      .sort((a, b) => a - b)
                      .forEach((mi) => {
                        const miKey = `${hKey}-${pad(mi)}`;
                        parent.set(miKey, hKey);
                        addChild(hKey, miKey);

                        if (maxLevel === "second") {
                          const secSet = minuteMap[mi];
                          const secKeys = Array.from(secSet);

                          const secNodes: DataNode[] = [];
                          secKeys
                            .sort((a, b) => a - b)
                            .forEach((s) => {
                              const sKey = `${miKey}-${pad(s)}`;
                              parent.set(sKey, miKey);
                              addChild(miKey, sKey);
                              secNodes.push({
                                key: sKey,
                                title: `${pad(s)}秒`,
                                isLeaf: true,
                              });
                              leafKeys.push(sKey);
                            });
                          minNodes.push({
                            key: miKey,
                            title: `${pad(mi)}分`,
                            children: secNodes,
                          });
                        } else {
                          /* phút làm lá */
                          minNodes.push({
                            key: miKey,
                            title: `${pad(mi)}分`,
                            isLeaf: true,
                          });
                          leafKeys.push(miKey);
                        }
                      });

                    hourNodes.push({
                      key: hKey,
                      title: `${hStr}時`,
                      children: minNodes,
                    });
                  } else if (isOnlyHourZero) {
                    parent.delete(hKey);
                    const currentChildren = children.get(dKey) || [];
                    children.set(
                      dKey,
                      currentChildren.filter((c) => c !== hKey),
                    );
                    return;
                  } else {
                    /* -------- Hour là lá -------- */
                    hourNodes.push({
                      key: hKey,
                      title: `${hStr}時`,
                      isLeaf: true,
                    });
                    leafKeys.push(hKey);
                  }
                });

              dayNodes.push({
                key: dKey,
                title: `${dStr}日`,
                children: hourNodes,
              });
              addChild(
                dKey,
                hourNodes.map((n) => String(n.key)),
              );
            });

          monthNodes.push({
            key: mKey,
            title: `${mStr}月`,
            children: dayNodes,
          });
          addChild(
            mKey,
            dayNodes.map((n) => String(n.key)),
          );
        });

      years.push({
        key: yKey,
        title: `${yStr}年`,
        children: monthNodes,
      });
      addChild(
        yKey,
        monthNodes.map((n) => String(n.key)),
      );
    });

  return { tree: years, parent, children, leafKeys };
}

export function formatKeyToJP(key: string): string {
  const seg = key.split("-").map((s) => s.padStart(2, "0"));
  const [y, M = "01", d = "01", H = "00", m = "00", s = "00"] = seg;

  switch (seg.length) {
    case 1: // year
      return `${y}年`;
    case 2: // year-month
      return dayjs(`${y}-${M}-01`, "YYYY-MM-DD").format("YYYY年MM月");
    case 3: // year-month-day
      return dayjs(`${y}-${M}-${d}`, "YYYY-MM-DD").format("YYYY年MM月DD日");
    case 4: // year-month-day-hour
      return dayjs(`${y}-${M}-${d} ${H}:00:00`, "YYYY-MM-DD HH:mm:ss").format(
        "YYYY年MM月DD日 HH",
      );
    case 5: // year-month-day-hour-minute
      return dayjs(`${y}-${M}-${d} ${H}:${m}:00`, "YYYY-MM-DD HH:mm:ss").format(
        "YYYY年MM月DD日 HH:mm",
      );
    default: // second or anything else -> full
      return dayjs(
        `${y}-${M}-${d} ${H}:${m}:${s}`,
        "YYYY-MM-DD HH:mm:ss",
      ).format("YYYY年MM月DD日 HH:mm:ss");
  }
}

export function formatKeyToAPI(key: string, mode?: string): string {
  const seg = key.split("-").map((s) => s.padStart(2, "0"));
  if (mode === "time") {
    // time mode keys are HH or HH-mm
    if (seg.length === 1) return `${seg[0]}`;
    if (seg.length === 2) return `${seg[0]}:${seg[1]}`;
    return key;
  }

  const [y, M, d, H, m, s] = seg;

  switch (seg.length) {
    case 1: // year
      return `${y}`;
    case 2: // year-month
      return `${y}-${M}`;
    case 3: // year-month-day
      return `${y}-${M}-${d}`;
    case 4: // year-month-day-hour
      return `${y}-${M}-${d} ${H}:00`;
    case 5: // year-month-day-hour-minute
      return `${y}-${M}-${d} ${H}:${m}`;
    default: // second or anything else -> full
      return `${y}-${M}-${d} ${H}:${m}:${s}`;
  }
}

export function formatRawDateToAPI(
  dateStr: string | number | Date,
  mode?: string,
): string | null {
  if (typeof dateStr === "string") {
    let trimmed = dateStr.trim();

    // Normalize Japanese date format (e.g., "2024年01月15日" → "2024-01-15")
    trimmed = formatJapaneseDateValue(trimmed);

    if (mode === "time") {
      // Match HH or HH:mm format
      if (/^\d+(?::\d{1,2})?$/.test(trimmed)) {
        const parts = trimmed.split(":");
        if (parts.length === 1) {
          return parts[0].padStart(2, "0");
        }
        return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
      }
    } else {
      // Match partial date formats: yyyy, yyyy-mm, yyyy-mm-dd, yyyy-mm-dd hh, yyyy-mm-dd hh:mm
      const dateRegex =
        /^(\d{4})([-/](\d{1,2}))?([-/](\d{1,2}))?(\s+(\d{1,2})(:\d{1,2})?)?$/;
      const match = trimmed.match(dateRegex);
      if (match) {
        const [, year, , month, , day, , hour, minute] = match;

        if (
          ["date_and_time", "created_datetime", "updated_datetime"].includes(
            mode || "",
          ) &&
          hour &&
          minute
        ) {
          const isoDate = `${year}-${(month || "01").padStart(2, "0")}-${(
            day || "01"
          ).padStart(2, "0")} ${hour.padStart(2, "0")}${minute}:00`;
          return dayjs(isoDate).format("YYYY-MM-DDTHH:mm:ss+09:00");
        }

        let result = year;

        if (month) {
          result += `-${month.padStart(2, "0")}`;
          if (day) {
            result += `-${day.padStart(2, "0")}`;
            if (hour) {
              result += ` ${hour.padStart(2, "0")}`;
              if (minute) {
                result += minute; // already includes colon
              } else {
                result += ":00";
              }
            }
          }
        }
        return result;
      }
    }
  }

  // Fallback to dayjs parsing for other formats
  const d = typeof dateStr === "string" ? dayjs(dateStr) : dayjs(dateStr);
  if (!d.isValid()) return null;

  if (mode === "time") {
    return d.format("HH:mm");
  }
  if (mode === "date") {
    return d.format("YYYY-MM-DD");
  }
  if (
    ["date_and_time", "created_datetime", "updated_datetime"].includes(
      mode || "",
    )
  ) {
    if (typeof dateStr === "string" && dateStr.includes("T")) {
      return dateStr;
    }
    return d.format("YYYY-MM-DDTHH:mm:ss+09:00");
  }
  return d.format("YYYY-MM-DD HH:mm:ss");
}

const formatInitialChecked = (
  checkedDates: string[],
  mode?: string,
): string[] => {
  const result = new Set<string>();

  checkedDates.forEach((dateStr) => {
    const raw = String(dateStr).trim();
    // Parse date string
    let d = dayjs(
      raw,
      [
        FMT_SEC,
        FMT_MIN,
        FMT_HOUR,
        FMT_MIN_NOSPACE,
        FMT_HOUR_NOSPACE,
        "YYYY年MM月DD日",
        "YYYY年M月D日",
        "YYYY年MM月D日",
        "YYYY年M月DD日",
        "YYYY年MM月",
        "YYYY年M月",
        "YYYY年",
        "YYYY年MM月DD日HH:mm",
        "YYYY-MM-DD",
        "YYYY/MM/DD",
        "YYYY年MM月DD日",
        "YYYY-MM",
      ],
      true,
    );

    if (!d.isValid()) d = dayjs(raw);
    if (mode === "time") {
      let hour = 0;
      let minute = 0;
      let second = 0;
      let isValidTime = false;

      if (/^\d+:[0-5]\d$/.test(raw)) {
        hour = parseInt(raw.split(":")[0], 10);
        minute = parseInt(raw.split(":")[1], 10);
        isValidTime = true;
      } else if (/^\d+$/.test(raw)) {
        hour = parseInt(raw, 10);
        isValidTime = true;
      } else if (d.isValid()) {
        hour = d.hour();
        minute = d.minute();
        second = d.second();
        isValidTime = true;
      }

      if (isValidTime) {
        const hourKey = `${hour.toString().padStart(2, "0")}`;
        result.add(hourKey);
        if (minute !== 0 || second !== 0 || /^\d+:[0-5]\d$/.test(raw)) {
          const minuteKey = `${hourKey}-${minute.toString().padStart(2, "0")}`;
          result.add(minuteKey);
        }
      }
      return;
    }

    if (!d.isValid()) return;

    const year = d.year();
    const month = d.month() + 1;
    const day = d.date();
    const hour = d.hour();
    const minute = d.minute();
    const second = d.second();

    const isYearOnly = /^\d{4}年$/.test(raw) || /^\d{4}$/.test(raw);
    const isYearMonthOnly =
      /^\d{4}年\d{1,2}月$/.test(raw) || /^\d{4}-\d{1,2}$/.test(raw);
    const isDayOnly =
      /^\d{4}年\d{1,2}月\d{1,2}日$/.test(raw) ||
      /^\d{4}-\d{1,2}-\d{1,2}$/.test(raw) ||
      /^\d{4}\/\d{1,2}\/\d{1,2}$/.test(raw);

    // Always add year
    const yearKey = `${year}`;
    const monthKey = `${year}-${month.toString().padStart(2, "0")}`;
    const dayKey = `${year}-${month.toString().padStart(2, "0")}-${day
      .toString()
      .padStart(2, "0")}`;

    result.add(yearKey);
    if (isYearOnly) return;

    result.add(monthKey);
    if (isYearMonthOnly) return;

    result.add(dayKey);
    if (isDayOnly) return;

    const hasTime =
      hour !== 0 ||
      minute !== 0 ||
      second !== 0 ||
      raw.includes(" ") ||
      raw.includes("T") ||
      /日\s*\d/.test(raw);
    if (hasTime) {
      const hourKey = `${dayKey}-${hour.toString().padStart(2, "0")}`;
      result.add(hourKey);

      const hasMinute = minute !== 0 || second !== 0 || raw.includes(":");
      if (hasMinute) {
        const minuteKey = `${hourKey}-${minute.toString().padStart(2, "0")}`;
        result.add(minuteKey);

        const hasSecond = second !== 0 || raw.split(":").length > 2;
        if (hasSecond) {
          const secondKey = `${minuteKey}-${second
            .toString()
            .padStart(2, "0")}`;
          result.add(secondKey);
        }
      }
    }
  });

  return Array.from(result);
};

/* ---------- 2. Component ------------------------------------------- */
interface Props {
  mode?: string;
  timestamps: (string | number | Date)[];
  initialChecked?: string[]; // yyyy-MM-dd-HH
  onChange?: (
    leafKeys: string[],
    originalValues?: string[],
    apiKeys?: string[],
  ) => void;
  defaultExpandAll?: boolean;
  parseClockTime?: boolean;
}

const DateHierarchyFilter: React.FC<Props> = ({
  mode,
  timestamps,
  initialChecked = [],
  onChange,
  defaultExpandAll = true,
  parseClockTime = false,
}) => {
  /* build tree + maps  */
  const { tree, parent, children, leafValueMap } = useMemo(() => {
    if (mode === "time") {
      return buildTimeTreeData(timestamps, parseClockTime);
    }
    const res = buildTreeData(timestamps, mode);
    const valMap = new Map<string, Set<string>>();

    const pad = (n: number) => (n < 10 ? "0" + n : "" + n);
    for (const item of timestamps) {
      const d = typeof item === "string" ? dayjs(item) : dayjs(item);
      if (!d.isValid()) continue;
      const str = typeof item === "string" ? item : d.toISOString();
      const y = d.year(),
        m = d.month() + 1,
        dd = d.date(),
        h = d.hour(),
        mi = d.minute();
      const yKey = `${y}`;
      const mKey = `${yKey}-${pad(m)}`;
      const dKey = `${mKey}-${pad(dd)}`;
      const hKey = `${dKey}-${pad(h)}`;
      const miKey = `${hKey}-${pad(mi)}`;
      const sKey = `${miKey}-${pad(d.second())}`;

      const possibleKeys = [sKey, miKey, hKey, dKey, mKey, yKey];
      for (const pk of possibleKeys) {
        if (res.leafKeys.includes(pk)) {
          const set = valMap.get(pk) || new Set();
          set.add(str);
          valMap.set(pk, set);
          break;
        }
      }
    }
    return { ...res, leafValueMap: valMap };
  }, [timestamps, mode, parseClockTime]);

  const [checked, setChecked] = useState<string[]>(
    formatInitialChecked(initialChecked || [], mode) || [],
  );

  useEffect(() => {
    if (initialChecked) {
      const formatted = formatInitialChecked(initialChecked, mode);
      setChecked(formatted);
    }
  }, [initialChecked, mode]);

  const getDesc = (root: string): string[] => {
    const out: string[] = [];
    const st = children.get(root) ? children?.get(root)?.slice() : [];
    while (st?.length) {
      const k: any = st.pop();
      out.push(k);
      const kids = children.get(k);
      if (kids && kids.length) Array.prototype.push.apply(st, kids); // ES5-safe
    }
    return out;
  };

  const handleCheck: TreeProps["onCheck"] = (checkedParam, info) => {
    const pool = new Set<string>(
      Array.isArray(checkedParam)
        ? (checkedParam as string[])
        : ((checkedParam as any).checked as string[]),
    );

    const key = String(info.node.key);
    const willCheck = info.checked;

    if (!willCheck) {
      pool.delete(key);
      const prefix = key + "-";
      pool.forEach((k) => {
        if (k.startsWith(prefix)) pool.delete(k);
      });
      getDesc(key).forEach((k) => pool.delete(k));
      Array.from(pool).forEach((k) => {
        if (k.startsWith(`${key}-`)) pool.delete(k);
      });
      for (let p = parent.get(key); p; p = parent.get(p)) {
        pool.delete(p);
      }
    } else {
      pool.add(key);
    }

    pool.forEach((k) => {
      for (let p = parent.get(k); p; p = parent.get(p)) pool.add(p);
    });

    const next = Array.from(pool);
    setChecked(next);
    const terminal = next.filter(
      (k) => !(children.get(k) ?? []).some((c) => pool.has(c)),
    );
    const originalValues = Array.from(
      new Set(terminal.flatMap((k) => Array.from(leafValueMap.get(k) || []))),
    );
    const apiKeys = terminal.flatMap((k) => {
      const seg = k.split("-");
      if (
        (["date_and_time", "created_datetime", "updated_datetime"].includes(
          mode || "",
        ) &&
          seg.length >= 4) ||
        (mode === "time" && seg.length >= 1)
      ) {
        const descendants = getDesc(k);
        const leaves = [k, ...descendants].filter((desc) =>
          leafValueMap.has(desc),
        );
        return leaves.flatMap((leaf) =>
          Array.from(leafValueMap.get(leaf) || []),
        );
      }
      return [formatKeyToAPI(k, mode)];
    });
    onChange?.(
      terminal.map((k) => (mode === "time" ? k : formatKeyToJP(k))),
      originalValues,
      apiKeys,
    );
  };

  return (
    <>
      <Tree
        checkable
        checkStrictly
        defaultExpandAll={defaultExpandAll}
        showLine
        treeData={tree}
        checkedKeys={checked}
        onCheck={handleCheck}
        // style={{ maxHeight: 400, overflow: "auto" }}
      />
    </>
  );
};

export default DateHierarchyFilter;
